import { useState } from 'react';
import { Phone, Smartphone } from 'lucide-react';
import { isMobileDevice, useTelephonyStore } from '../../store/telephonyStore';

interface CallButtonProps {
  number?: string | null;
  contactId?: string | null;
  accountId?: string | null;
  /** Placed for an Otto mission (its call request card): the call is linked to it. */
  missionThreadId?: string | null;
  /**
   * Called once a « sur mon mobile » call is placed: its row exists, and a card that lists
   * calls re-reads it. (A softphone call is followed through the store's `phase`.)
   */
  onPlaced?: () => void;
  label?: string;
  className?: string;
}

/**
 * Click-to-call from a CRM record. Two ways:
 *  - "Appeler": in-app softphone (WebRTC), best at the desk.
 *  - mobile icon: "call my phone then dial" (bridge) — Twilio rings the owner's
 *    mobile then connects the client; ideal away from the PC. Both are recorded.
 * On a phone the softphone can't run (see isMobileDevice): only the bridge shows.
 */
export function CallButton({
  number,
  contactId,
  accountId,
  missionThreadId,
  onPlaced,
  label,
  className,
}: CallButtonProps) {
  const startCall = useTelephonyStore((s) => s.startCall);
  const bridgeCall = useTelephonyStore((s) => s.bridgeCall);
  const phase = useTelephonyStore((s) => s.phase);
  const [bridgeMsg, setBridgeMsg] = useState<string | null>(null);
  const busy = phase !== 'idle' && phase !== 'ended';
  const softphoneDisabled = !number || busy;

  const onBridge = async () => {
    if (!number) return;
    setBridgeMsg('Appel…');
    const ok = await bridgeCall(number, { contactId, accountId, missionThreadId });
    setBridgeMsg(ok ? 'Ton mobile va sonner 📲' : 'Échec');
    if (ok) onPlaced?.();
    window.setTimeout(() => setBridgeMsg(null), 4000);
  };

  const onPhone = isMobileDevice();

  return (
    <span className={`inline-flex items-center gap-1 ${className ?? ''}`}>
      {!onPhone && (
        <button
          type="button"
          onClick={() => number && startCall(number, { contactId, accountId, missionThreadId })}
          disabled={softphoneDisabled}
          title={number ? `Appeler ${number} (softphone)` : 'Aucun numéro'}
          className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
            softphoneDisabled
              ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
              : 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950'
          }`}
        >
          <Phone className="h-4 w-4" />
          {label ?? 'Appeler'}
        </button>
      )}
      <button
        type="button"
        onClick={onBridge}
        disabled={!number}
        title="M'appeler sur mon mobile, puis composer le numéro (idéal hors du PC)"
        className={`inline-flex items-center justify-center gap-1.5 rounded-md p-1.5 text-sm font-medium transition-colors ${
          number
            ? 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950'
            : 'cursor-not-allowed text-gray-400 dark:text-gray-600'
        }`}
      >
        <Smartphone className="h-4 w-4" />
        {onPhone && (label ?? 'Appeler')}
      </button>
      {bridgeMsg && <span className="text-xs text-neutral-500 dark:text-gray-400">{bridgeMsg}</span>}
    </span>
  );
}
